/**
 * LocalStorage caching service for Shipments and Shipment Items.
 * Provides fast access to shipment data that doesn't change frequently.
 */

import type { AirtableShipment, AirtableShipmentItem } from '../types/airtableTypes';
import { getShipmentCacheReadNamespace } from './receivingAPI';

interface CachedShipmentDetail {
  shipment: AirtableShipment;
  shipmentItems: AirtableShipmentItem[];
}

const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

const shipmentsListKey = (): string => `rsv_shipments_list_${getShipmentCacheReadNamespace()}`;

const cacheTimestampKey = (): string => `rsv_shipments_cache_ts_${getShipmentCacheReadNamespace()}`;

const shipmentDetailKey = (shipmentId: string): string =>
  `rsv_shipment_items_${getShipmentCacheReadNamespace()}_${shipmentId}`;

const getCacheTimestamp = (): number | null => {
  const ts = localStorage.getItem(cacheTimestampKey());
  return ts ? parseInt(ts, 10) : null;
};

export const isCacheValid = (): boolean => {
  const ts = getCacheTimestamp();
  if (!ts) return false;
  return Date.now() - ts < CACHE_TTL_MS;
};

const touchCacheTimestamp = (): void => {
  localStorage.setItem(cacheTimestampKey(), Date.now().toString());
};

export const getCachedShipments = (): AirtableShipment[] | null => {
  try {
    const data = localStorage.getItem(shipmentsListKey());
    if (!data) return null;
    return JSON.parse(data) as AirtableShipment[];
  } catch (e) {
    console.warn('Failed to parse cached shipments:', e);
    return null;
  }
};

export const cacheShipments = (shipments: AirtableShipment[]): void => {
  try {
    localStorage.setItem(shipmentsListKey(), JSON.stringify(shipments));
    touchCacheTimestamp();
  } catch (e) {
    console.warn('Failed to cache shipments:', e);
  }
};

export const getCachedShipmentDetail = (shipmentId: string): CachedShipmentDetail | null => {
  try {
    const data = localStorage.getItem(shipmentDetailKey(shipmentId));
    if (!data) return null;
    return JSON.parse(data) as CachedShipmentDetail;
  } catch (e) {
    console.warn('Failed to parse cached shipment detail:', e);
    return null;
  }
};

export const cacheShipmentDetail = (
  shipmentId: string,
  shipment: AirtableShipment,
  shipmentItems: AirtableShipmentItem[]
): void => {
  try {
    localStorage.setItem(shipmentDetailKey(shipmentId), JSON.stringify({ shipment, shipmentItems }));
  } catch (e) {
    console.warn('Failed to cache shipment detail:', e);
  }
};

/** Removes legacy (non-namespaced) keys and all namespaced receiving cache keys. */
export const clearShipmentCache = (): void => {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;
      if (
        key === 'rsv_shipments_list' ||
        key === 'rsv_shipments_cache_ts' ||
        key.startsWith('rsv_shipments_list_') ||
        key.startsWith('rsv_shipments_cache_ts_') ||
        key.startsWith('rsv_shipment_items_')
      ) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
  } catch (e) {
    console.warn('Failed to clear shipment cache:', e);
  }
};

export const invalidateShipmentDetail = (shipmentId: string): void => {
  try {
    localStorage.removeItem(shipmentDetailKey(shipmentId));
  } catch (e) {
    console.warn('Failed to invalidate shipment detail cache:', e);
  }
};

export const prefetchShipmentDetails = async (
  shipments: AirtableShipment[],
  fetchShipmentFn: (shipmentId: string) => Promise<CachedShipmentDetail | null>
): Promise<void> => {
  const prefetchPromises = shipments.map(async (shipment) => {
    if (getCachedShipmentDetail(shipment.id)) {
      return;
    }

    try {
      const data = await fetchShipmentFn(shipment.id);
      if (data) {
        cacheShipmentDetail(shipment.id, data.shipment, data.shipmentItems);
      }
    } catch (e) {
      console.warn(`Failed to prefetch shipment ${shipment.id}:`, e);
    }
  });

  await Promise.all(prefetchPromises);
};
